// =======================================================
// MedicineManager.jsx
// COMPLETE UPDATED CODE
// IMAGE URL FIELD ADDED
// CREATE + EDIT + TABLE + DETAILS DIALOG
// =======================================================

import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import {
  Box,
  TextField,
  Button,
 Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Pagination,
  Autocomplete,
  Slider,
  Collapse,
} from "@mui/material";

import {
  Edit,
  Delete,
  Search,
  AddCircleOutline,
  Save,
  Cancel,
  LocalPharmacy,
  FilterAlt,
  RestartAlt,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility,
} from "@mui/icons-material";

import { motion } from "framer-motion";

import AdminSidebar from "./AdminSidebar";
import { BASE_URL } from "../backend/backend.js";

const API_BASE_URL = BASE_URL;

const MEDICINE_TYPES = [
  { label: "Allopathy", value: "allopathy" },
  { label: "Ayurvedic", value: "ayurvedic" },
  { label: "Homeopathy", value: "homeopathy" },
  { label: "Supplement", value: "supplement" },
  { label: "Other", value: "other" },
];

const emptyMedicine = {
  medicineName: "",
  price: "",
  medineType: "",
  description: "",
  discountPercentage: "",
  descountPrice: "",
  totalPiece: "",
  expiryDate: "",
  manufacturerName: "",
  packSizeLabel: "",
  shortComposition1: "",
  shortComposition2: "",
  isDiscontinued: "FALSE",

  // NEW FIELD
  imageUrl: "",
};

const getDisplayName = (medicine) => {
  if (medicine?.medicineName?.trim()) {
    return medicine.medicineName;
  }

  const comp1 = (
    medicine.shortComposition1 || ""
  ).trim();

  const comp2 = (
    medicine.shortComposition2 || ""
  ).trim();

  if (comp1 && comp2)
    return `${comp1} + ${comp2}`;

  if (comp1) return comp1;

  if (comp2) return comp2;

  return "Unnamed Medicine";
};

const MedicineManager = ({
  sidebar = true,
  embedded = false,
  height = "100vh",
}) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("md")
  );

  const [medicines, setMedicines] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [openDialog, setOpenDialog] =
    useState(false);

  const [manufacturerFilter, setManufacturerFilter] =
    useState("");

  const [typeFilter, setTypeFilter] =
    useState("");

  const [stockFilter, setStockFilter] =
    useState("");

  const [discountRange, setDiscountRange] =
    useState([0, 100]);

  const [priceRange, setPriceRange] =
    useState([0, 10000]);

  const [showFilters, setShowFilters] =
    useState(false);

  const [isEditMode, setIsEditMode] =
    useState(false);

  const [selectedId, setSelectedId] =
    useState(null);

  const [formData, setFormData] =
    useState(emptyMedicine);

  // DETAILS DIALOG
  const [detailsMedicine, setDetailsMedicine] =
    useState(null);

  // PAGINATION

  const [page, setPage] = useState(0);

  const [totalPages, setTotalPages] =
    useState(0);

  const pageSize = 50;

  // =======================================================
  // FETCH MEDICINES
  // =======================================================

  const fetchMedicines = async (
    currentPage = 0,
    search = ""
  ) => {
    try {
      setLoading(true);

      let url = "";

      if (search.trim()) {
        url = `${API_BASE_URL}/api/filterMedicine?name=${encodeURIComponent(
          search
        )}&page=${currentPage}&size=${pageSize}`;
      } else {
        url = `${API_BASE_URL}/api/getAllMedicine?page=${currentPage}&size=${pageSize}`;
      }

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type":
            "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          "Failed to fetch medicines"
        );
      }

      const data = await response.json();

      const medicineList = Array.isArray(
        data
      )
        ? data
        : data.content || [];

      const processedData =
        medicineList.map((med) => ({
          ...med,
          displayName:
            getDisplayName(med),
        }));

      setMedicines(processedData);

      setTotalPages(
        data.totalPages || 1
      );
    } catch (error) {
      console.error(error);
      setMedicines([]);
    } finally {
      setLoading(false);
    }
  };

  // =======================================================
  // SEARCH
  // =======================================================

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchMedicines(page, searchTerm);
    }, 500);

    return () =>
      clearTimeout(debounce);
  }, [page, searchTerm]);

  // =======================================================
  // FILTERS
  // =======================================================

  const filteredMedicines =
    useMemo(() => {
      return medicines.filter((med) => {
        const matchesManufacturer =
          !manufacturerFilter ||
          med.manufacturerName
            ?.toLowerCase()
            .includes(
              manufacturerFilter.toLowerCase()
            );

        const matchesType =
          !typeFilter ||
          med.medineType === typeFilter;

        const matchesDiscount =
          Number(
            med.discountPercentage || 0
          ) >= discountRange[0] &&
          Number(
            med.discountPercentage || 0
          ) <= discountRange[1];

        const matchesPrice =
          Number(med.price || 0) >=
            priceRange[0] &&
          Number(med.price || 0) <=
            priceRange[1];

        const matchesStock =
          stockFilter === ""
            ? true
            : stockFilter === "instock"
            ? med.totalPiece > 0
            : med.totalPiece <= 0;

        return (
          matchesManufacturer &&
          matchesType &&
          matchesDiscount &&
          matchesPrice &&
          matchesStock
        );
      });
    }, [
      medicines,
      manufacturerFilter,
      typeFilter,
      discountRange,
      priceRange,
      stockFilter,
    ]);

  const manufacturerOptions =
    useMemo(() => {
      const manufacturers =
        medicines
          .map(
            (med) =>
              med.manufacturerName
          )
          .filter(Boolean);

      return [...new Set(manufacturers)];
    }, [medicines]);

  // =======================================================
  // FORM
  // =======================================================

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.value,
    }));
  };

  const openAddDialog = () => {
    setFormData(emptyMedicine);

    setSelectedId(null);

    setIsEditMode(false);

    setOpenDialog(true);
  };

  const openEditDialog = (
    medicine
  ) => {
    setSelectedId(medicine.id);

    setFormData({
      ...medicine,
      expiryDate:
        medicine.expiryDate || "",
      isDiscontinued:
        medicine.isDiscontinued ||
        "FALSE",

      imageUrl:
        medicine.imageUrl || "",
    });

    setIsEditMode(true);

    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);

    setFormData(emptyMedicine);

    setSelectedId(null);
  };

  // =======================================================
  // PAYLOAD
  // =======================================================

  const buildPayload = () => ({
    id: selectedId || 0,

    medicineName:
      formData.medicineName,

    price:
      parseFloat(
        formData.price
      ) || 0,

    medineType:
      formData.medineType,

    description:
      formData.description,

    discountPercentage:
      parseFloat(
        formData.discountPercentage
      ) || 0,

    descountPrice:
      parseFloat(
        formData.descountPrice
      ) || 0,

    totalPiece:
      parseFloat(
        formData.totalPiece
      ) || 0,

    expiryDate:
      formData.expiryDate,

    manufacturerName:
      formData.manufacturerName,

    packSizeLabel:
      formData.packSizeLabel,

    shortComposition1:
      formData.shortComposition1,

    shortComposition2:
      formData.shortComposition2,

    isDiscontinued:
      formData.isDiscontinued,

    // NEW FIELD
    imageUrl: formData.imageUrl,
  });

  // =======================================================
  // SAVE
  // =======================================================

  const handleSubmit = async () => {
    try {
      const payload =
        buildPayload();

      const url = isEditMode
        ? `${API_BASE_URL}/admin/updateMedicine/${selectedId}`
        : `${API_BASE_URL}/admin/saveMedicine`;

      const method = isEditMode
        ? "PUT"
        : "POST";

      const response = await fetch(
        url,
        {
          method,
          credentials: "include",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(
            payload
          ),
        }
      );

      const text =
        await response.text();

      if (!response.ok) {
        throw new Error(text);
      }

      alert(
        isEditMode
          ? "Medicine Updated Successfully"
          : "Medicine Added Successfully"
      );

      closeDialog();

      fetchMedicines(
        page,
        searchTerm
      );
    } catch (error) {
      console.error(error);

      alert(error.message);
    }
  };

  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete = async (
    id
  ) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to delete this medicine?"
      );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/deleteMedicine/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const text =
        await response.text();

      if (!response.ok) {
        throw new Error(text);
      }

      alert(
        "Medicine Deleted Successfully"
      );

      fetchMedicines(
        page,
        searchTerm
      );
    } catch (error) {
      console.error(error);

      alert(error.message);
    }
  };

  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setManufacturerFilter("");

    setTypeFilter("");

    setStockFilter("");

    setDiscountRange([0, 100]);

    setPriceRange([0, 10000]);
  };

  return (
    <Box
      sx={{
        display: "flex",

        minHeight: embedded
          ? "auto"
          : height,

        width: "100%",

        background:
          "linear-gradient(to right, #f8fafc, #eef2ff)",
      }}
    >
      {sidebar &&
        !embedded && (
          <AdminSidebar />
        )}

      <Box
        sx={{
          flexGrow: 1,

          p: {
            xs: 2,
            md: 4,
          },
        }}
      >
        {/* HEADER */}

        <Paper
          sx={{
            p: 4,

            borderRadius: 5,

            mb: 3,

            background:
              "linear-gradient(135deg,#2563eb,#7c3aed)",

            color: "white",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            spacing={3}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
            >
              <Avatar
                sx={{
                  width: 70,
                  height: 70,

                  bgcolor:
                    "rgba(255,255,255,0.15)",
                }}
              >
                <LocalPharmacy fontSize="large" />
              </Avatar>

              <Box>
                <Typography
                  variant="h4"
                  fontWeight={800}
                >
                  Medicine Inventory
                </Typography>

                <Typography>
                  AI Powered Medicine
                  Management
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={
                <AddCircleOutline />
              }
              onClick={
                openAddDialog
              }
              sx={{
                borderRadius: 999,

                bgcolor: "white",

                color: "#2563eb",

                fontWeight: 700,

                px: 4,

                "&:hover": {
                  bgcolor:
                    "#f1f5f9",
                },
              }}
            >
              Add Medicine
            </Button>
          </Stack>
        </Paper>

        {/* SEARCH */}

        <Paper
          sx={{
            p: 3,

            mb: 3,

            borderRadius: 5,

            border:
              "1px solid #e2e8f0",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search medicine, company, composition..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(
                    e.target.value
                  );

                  setPage(0);
                }}
                InputProps={{
                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Search
                          sx={{
                            color:
                              "#2563eb",
                          }}
                        />
                      </InputAdornment>
                    ),

                  sx: {
                    borderRadius:
                      "999px",

                    bgcolor:
                      "white",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  <FilterAlt />
                }
                endIcon={
                  showFilters ? (
                    <KeyboardArrowUp />
                  ) : (
                    <KeyboardArrowDown />
                  )
                }
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                sx={{
                  borderRadius: 999,

                  height: "56px",

                  background:
                    "linear-gradient(135deg,#2563eb,#7c3aed)",
                }}
              >
                Advanced Filters
              </Button>
            </Grid>
          </Grid>

          {/* FILTERS */}

          <Collapse in={showFilters}>
            <Box mt={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    freeSolo
                    options={
                      manufacturerOptions
                    }
                    value={
                      manufacturerFilter
                    }
                    onInputChange={(
                      e,
                      value
                    ) =>
                      setManufacturerFilter(
                        value
                      )
                    }
                    renderInput={(
                      params
                    ) => (
                      <TextField
                        {...params}
                        label="Manufacturer"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>
                      Medicine Type
                    </InputLabel>

                    <Select
                      value={
                        typeFilter
                      }
                      label="Medicine Type"
                      onChange={(e) =>
                        setTypeFilter(
                          e.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        All
                      </MenuItem>

                      {MEDICINE_TYPES.map(
                        (
                          type
                        ) => (
                          <MenuItem
                            key={
                              type.value
                            }
                            value={
                              type.value
                            }
                          >
                            {
                              type.label
                            }
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>
                      Stock
                    </InputLabel>

                    <Select
                      value={
                        stockFilter
                      }
                      label="Stock"
                      onChange={(e) =>
                        setStockFilter(
                          e.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        All
                      </MenuItem>

                      <MenuItem value="instock">
                        In Stock
                      </MenuItem>

                      <MenuItem value="outstock">
                        Out Of Stock
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography
                    fontWeight={700}
                    mb={1}
                  >
                    Discount %
                  </Typography>

                  <Slider
                    value={
                      discountRange
                    }
                    onChange={(
                      e,
                      value
                    ) =>
                      setDiscountRange(
                        value
                      )
                    }
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography
                    fontWeight={700}
                    mb={1}
                  >
                    Price ₹
                  </Typography>

                  <Slider
                    value={
                      priceRange
                    }
                    onChange={(
                      e,
                      value
                    ) =>
                      setPriceRange(
                        value
                      )
                    }
                    valueLabelDisplay="auto"
                    min={0}
                    max={10000}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={
                      <RestartAlt />
                    }
                    onClick={
                      resetFilters
                    }
                    sx={{
                      borderRadius: 999,
                    }}
                  >
                    Reset Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>

        {/* LOADING */}

        {loading ? (
          <Paper
            sx={{
              p: 10,

              textAlign:
                "center",

              borderRadius: 5,
            }}
          >
            <CircularProgress />
          </Paper>
        ) : (
          <>
            {/* TABLE */}

            <Paper
              sx={{
                borderRadius: 5,

                overflow:
                  "hidden",
              }}
            >
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        "Medicine",
                        "Image",
                        "Type",
                        "Price",
                        "Discount",
                        "Final Price",
                        "Stock",
                        "Manufacturer",
                        "Actions",
                      ].map((head) => (
                        <TableCell
                          key={head}
                          sx={{
                            bgcolor:
                              "#0f172a",

                            color:
                              "white",

                            fontWeight: 700,
                          }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredMedicines.map(
                      (med) => (
                        <TableRow
                          hover
                          key={med.id}
                          component={
                            motion.tr
                          }
                          whileHover={{
                            scale:
                              1.005,
                          }}
                        >
                          {/* NAME */}

                          <TableCell>
                            <Stack>
                              <Typography fontWeight={700}>
                                {
                                  med.displayName
                                }
                              </Typography>

                              <Typography variant="caption">
                                #
                                {
                                  med.id
                                }
                              </Typography>
                            </Stack>
                          </TableCell>

                          {/* IMAGE */}

                          <TableCell>
                            {med.imageUrl ? (
                              <Avatar
                                variant="rounded"
                                src={
                                  med.imageUrl.startsWith(
                                    "http"
                                  )
                                    ? med.imageUrl
                                    : `${API_BASE_URL}/${med.imageUrl}`
                                }
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 3,
                                }}
                              />
                            ) : (
                              <Avatar
                                variant="rounded"
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 3,
                                  bgcolor:
                                    "#e0e7ff",
                                }}
                              >
                                <LocalPharmacy />
                              </Avatar>
                            )}
                          </TableCell>

                          {/* TYPE */}

                          <TableCell>
                            <Chip
                              label={
                                med.medineType
                              }
                              sx={{
                                textTransform:
                                  "capitalize",
                              }}
                            />
                          </TableCell>

                          {/* PRICE */}

                          <TableCell>
                            ₹
                            {Number(
                              med.price ||
                                0
                            ).toFixed(
                              2
                            )}
                          </TableCell>

                          {/* DISCOUNT */}

                          <TableCell>
                            <Chip
                              label={`${med.discountPercentage}%`}
                              color="success"
                            />
                          </TableCell>

                          {/* FINAL PRICE */}

                          <TableCell>
                            <Typography
                              fontWeight={700}
                              color="#16a34a"
                            >
                              ₹
                              {Number(
                                med.descountPrice ||
                                  0
                              ).toFixed(
                                2
                              )}
                            </Typography>
                          </TableCell>

                          {/* STOCK */}

                          <TableCell>
                            <Chip
                              label={
                                med.totalPiece
                              }
                              color={
                                med.totalPiece >
                                0
                                  ? "success"
                                  : "error"
                              }
                            />
                          </TableCell>

                          {/* MANUFACTURER */}

                          <TableCell>
                            {
                              med.manufacturerName
                            }
                          </TableCell>

                          {/* ACTIONS */}

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                            >
                              {/* DETAILS */}

                              <Tooltip title="More Details">
                                <IconButton
                                  onClick={() =>
                                    setDetailsMedicine(
                                      med
                                    )
                                  }
                                >
                                  <Visibility color="info" />
                                </IconButton>
                              </Tooltip>

                              {/* EDIT */}

                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() =>
                                    openEditDialog(
                                      med
                                    )
                                  }
                                >
                                  <Edit color="primary" />
                                </IconButton>
                              </Tooltip>

                              {/* DELETE */}

                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() =>
                                    handleDelete(
                                      med.id
                                    )
                                  }
                                >
                                  <Delete color="error" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* PAGINATION */}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={3}
              flexWrap="wrap"
              gap={2}
            >
              <Typography>
                Showing Page{" "}
                {page + 1} of{" "}
                {totalPages}
              </Typography>

              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(
                  e,
                  value
                ) =>
                  setPage(
                    value - 1
                  )
                }
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}

        {/* ADD / EDIT DIALOG */}

        <Dialog
          open={openDialog}
          onClose={closeDialog}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
            }}
          >
            {isEditMode
              ? "Update Medicine"
              : "Add Medicine"}
          </DialogTitle>

          <DialogContent dividers>
            <Grid
              container
              spacing={3}
            >
              {[
                {
                  label:
                    "Medicine Name",
                  name:
                    "medicineName",
                },
                {
                  label:
                    "Price",
                  name:
                    "price",
                  type:
                    "number",
                },
                {
                  label:
                    "Discount %",
                  name:
                    "discountPercentage",
                  type:
                    "number",
                },
                {
                  label:
                    "Discount Price",
                  name:
                    "descountPrice",
                  type:
                    "number",
                },
                {
                  label:
                    "Manufacturer",
                  name:
                    "manufacturerName",
                },
                {
                  label:
                    "Pack Size",
                  name:
                    "packSizeLabel",
                },
                {
                  label:
                    "Composition 1",
                  name:
                    "shortComposition1",
                },
                {
                  label:
                    "Composition 2",
                  name:
                    "shortComposition2",
                },

                // NEW FIELD
                {
                  label:
                    "Image URL",
                  name:
                    "imageUrl",
                },

                {
                  label:
                    "Stock",
                  name:
                    "totalPiece",
                  type:
                    "number",
                },
              ].map((field) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={
                    field.name
                  }
                >
                  <TextField
                    fullWidth
                    label={
                      field.label
                    }
                    name={
                      field.name
                    }
                    type={
                      field.type ||
                      "text"
                    }
                    value={
                      formData[
                        field
                          .name
                      ] || ""
                    }
                    onChange={
                      handleChange
                    }
                  />
                </Grid>
              ))}

              {/* DESCRIPTION */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  name="description"
                  value={
                    formData.description
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              {/* IMAGE PREVIEW */}

              {formData.imageUrl && (
                <Grid
                  item
                  xs={12}
                >
                  <Card
                    sx={{
                      borderRadius: 4,

                      border:
                        "1px solid #e2e8f0",
                    }}
                  >
                    <CardContent>
                      <Typography
                        fontWeight={
                          700
                        }
                        mb={2}
                      >
                        Medicine
                        Image
                        Preview
                      </Typography>

                      <Box
                        sx={{
                          display:
                            "flex",

                          justifyContent:
                            "center",
                        }}
                      >
                        <img
                          src={
                            formData.imageUrl.startsWith(
                              "http"
                            )
                              ? formData.imageUrl
                              : `${API_BASE_URL}/${formData.imageUrl}`
                          }
                          alt="medicine"
                          style={{
                            width:
                              "100%",

                            maxWidth:
                              "350px",

                            height:
                              "260px",

                            objectFit:
                              "cover",

                            borderRadius:
                              "20px",
                          }}
                          onError={(
                            e
                          ) => {
                            e.target.src =
                              "https://cdn-icons-png.flaticon.com/512/4320/4320337.png";
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
            }}
          >
            <Button
              variant="outlined"
              startIcon={
                <Cancel />
              }
              onClick={
                closeDialog
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={
                handleSubmit
              }
            >
              {isEditMode
                ? "Update"
                : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DETAILS DIALOG */}

        <Dialog
          open={Boolean(
            detailsMedicine
          )}
          onClose={() =>
            setDetailsMedicine(
              null
            )
          }
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
            }}
          >
            Medicine Details
          </DialogTitle>

          <DialogContent dividers>
            {detailsMedicine && (
              <Grid
                container
                spacing={3}
              >
                {/* IMAGE */}

                <Grid
                  item
                  xs={12}
                  md={5}
                >
                  <img
                    src={
                      detailsMedicine.imageUrl
                        ? detailsMedicine.imageUrl.startsWith(
                            "http"
                          )
                          ? detailsMedicine.imageUrl
                          : `${API_BASE_URL}/${detailsMedicine.imageUrl}`
                        : "https://cdn-icons-png.flaticon.com/512/4320/4320337.png"
                    }
                    alt="medicine"
                    style={{
                      width:
                        "100%",

                      height:
                        "320px",

                      objectFit:
                        "cover",

                      borderRadius:
                        "24px",
                    }}
                  />
                </Grid>

                {/* DETAILS */}

                <Grid
                  item
                  xs={12}
                  md={7}
                >
                  <Stack
                    spacing={2}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={
                        800
                      }
                    >
                      {
                        detailsMedicine.medicineName
                      }
                    </Typography>

                    <Divider />

                    <Typography>
                      <strong>
                        Type:
                      </strong>{" "}
                      {
                        detailsMedicine.medineType
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Manufacturer:
                      </strong>{" "}
                      {
                        detailsMedicine.manufacturerName
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Price:
                      </strong>{" "}
                      ₹
                      {
                        detailsMedicine.price
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Discount:
                      </strong>{" "}
                      {
                        detailsMedicine.discountPercentage
                      }
                      %
                    </Typography>

                    <Typography>
                      <strong>
                        Final Price:
                      </strong>{" "}
                      ₹
                      {
                        detailsMedicine.descountPrice
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Stock:
                      </strong>{" "}
                      {
                        detailsMedicine.totalPiece
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Pack Size:
                      </strong>{" "}
                      {
                        detailsMedicine.packSizeLabel
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Composition
                        1:
                      </strong>{" "}
                      {
                        detailsMedicine.shortComposition1
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Composition
                        2:
                      </strong>{" "}
                      {
                        detailsMedicine.shortComposition2
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Expiry
                        Date:
                      </strong>{" "}
                      {
                        detailsMedicine.expiryDate
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Description:
                      </strong>{" "}
                      {
                        detailsMedicine.description
                      }
                    </Typography>

                    <Typography>
                      <strong>
                        Image URL:
                      </strong>{" "}
                      {
                        detailsMedicine.imageUrl
                      }
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setDetailsMedicine(
                  null
                )
              }
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MedicineManager;